import { useState } from "react";
import { supabase } from "../../lib/supabase";

interface Account {
  id: string;
  school_name: string;
  email: string;
  license_count: number;
}

interface GiftResult {
  license_key: string;
  email_sent: boolean;
}

export function GiftKey() {
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<Account[] | null>(null);
  const [selected, setSelected] = useState<Account | null>(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [giftResult, setGiftResult] = useState<GiftResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSearch = async () => {
    const trimmed = search.trim();
    if (!trimmed) return;

    setSearching(true);
    setError(null);
    setSelected(null);
    setGiftResult(null);

    const { data, error: searchError } = await supabase
      .from("accounts")
      .select("id, school_name, email, licenses:licenses(count)")
      .or(`school_name.ilike.%${trimmed.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}%,email.ilike.%${trimmed.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}%`)
      .order("school_name")
      .limit(20);

    if (searchError) {
      setError("Search failed: " + searchError.message);
      setResults(null);
    } else {
      setResults(
        (data ?? []).map((a: any) => ({
          id: a.id,
          school_name: a.school_name,
          email: a.email,
          license_count: a.licenses?.[0]?.count ?? 0,
        })),
      );
    }
    setSearching(false);
  };

  const handleGift = async () => {
    if (!selected) return;

    setSubmitting(true);
    setError(null);

    const { data, error: giftError } = await supabase.functions.invoke(
      "gift-license",
      {
        body: { account_id: selected.id, note: note || undefined },
      },
    );

    if (giftError) {
      setError("Gift failed: " + giftError.message);
    } else {
      setGiftResult({
        license_key: data.license_key,
        email_sent: data.sent ?? false,
      });
    }
    setSubmitting(false);
  };

  const handleCopy = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard.");
    }
  };

  const handleReset = () => {
    setSearch("");
    setResults(null);
    setSelected(null);
    setNote("");
    setGiftResult(null);
    setError(null);
    setCopied(false);
  };

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-forest-900">
        Gift License Key
      </h1>
      <p className="mt-1 text-sm text-forest-500">
        Search for an account and gift them a license key.
      </p>

      {/* Success state */}
      {giftResult && (
        <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="font-display text-lg font-semibold text-emerald-800">
            License key gifted successfully
          </h2>
          <div className="mt-4 flex items-center gap-3">
            <code className="rounded-lg bg-white px-4 py-2 font-mono text-sm text-forest-900 ring-1 ring-emerald-200">
              {giftResult.license_key}
            </code>
            <button
              onClick={() => handleCopy(giftResult.license_key)}
              className="rounded-lg bg-forest-900 px-4 py-2 text-sm font-medium text-cream-100 hover:bg-forest-800"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <p className="mt-3 text-sm text-emerald-700">
            {giftResult.email_sent
              ? "A notification email was sent to the account holder."
              : "No notification email was sent."}
          </p>
          <button
            onClick={handleReset}
            className="mt-4 text-sm font-medium text-emerald-700 underline hover:text-emerald-800"
          >
            Gift another key
          </button>
        </div>
      )}

      {/* Search + results (hidden on success) */}
      {!giftResult && (
        <>
          {/* Search input */}
          <div className="mt-4 flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              placeholder="Search by school name or email..."
              className="paper-card w-full max-w-md rounded-lg border border-forest-200 bg-white px-4 py-2 text-sm text-forest-900 placeholder:text-forest-400 focus:border-forest-500 focus:outline-none"
            />
            <button
              onClick={handleSearch}
              disabled={searching || !search.trim()}
              className="rounded-lg bg-forest-900 px-4 py-2 text-sm font-medium text-cream-100 hover:bg-forest-800 disabled:opacity-50"
            >
              {searching ? "Searching..." : "Search"}
            </button>
          </div>

          {/* Error */}
          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

          {/* Search results */}
          {results !== null && !selected && (
            <div className="mt-6">
              {results.length === 0 ? (
                <p className="text-forest-500">
                  No accounts found matching your search.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-forest-500">
                    {results.length} account{results.length !== 1 && "s"} found.
                    Select one to gift a key.
                  </p>
                  {results.map((account) => (
                    <button
                      key={account.id}
                      onClick={() => {
                        setSelected(account);
                        setError(null);
                      }}
                      className="paper-card block w-full max-w-md rounded-lg border border-forest-200 bg-white px-4 py-3 text-left transition-colors hover:border-forest-400 hover:bg-cream-100"
                    >
                      <p className="font-medium text-forest-900">
                        {account.school_name}
                      </p>
                      <p className="mt-0.5 text-sm text-forest-500">
                        {account.email}
                      </p>
                      <p className="mt-0.5 text-xs text-forest-400">
                        {account.license_count} existing license
                        {account.license_count !== 1 && "s"}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected account detail + gift form */}
          {selected && (
            <div className="mt-6 max-w-md">
              <div className="paper-card rounded-lg border border-forest-200 bg-white p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-forest-900">
                      {selected.school_name}
                    </p>
                    <p className="mt-0.5 text-sm text-forest-500">
                      {selected.email}
                    </p>
                    <p className="mt-0.5 text-xs text-forest-400">
                      {selected.license_count} existing license
                      {selected.license_count !== 1 && "s"}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelected(null)}
                    className="text-sm text-forest-400 hover:text-forest-600"
                  >
                    Change
                  </button>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-forest-700">
                    Note (optional)
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Gift reason or internal note..."
                    rows={2}
                    className="paper-card mt-1 w-full rounded-lg border border-forest-200 bg-white px-3 py-2 text-sm text-forest-900 placeholder:text-forest-400 focus:border-forest-500 focus:outline-none"
                  />
                </div>

                <button
                  onClick={handleGift}
                  disabled={submitting}
                  className="mt-4 flex items-center gap-2 rounded-lg bg-forest-900 px-4 py-2 text-sm font-medium text-cream-100 hover:bg-forest-800 disabled:opacity-50"
                >
                  {submitting && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-cream-300 border-t-cream-100" />
                  )}
                  {submitting ? "Gifting..." : "Gift License Key"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
